// Copyright (c) Microsoft Corporation. All rights reserved.
// Licensed under the MIT License.
import { arrForEach, isFunction, objDefineProps } from "@nevware21/ts-utils";
import { _DYN_CALL, _DYN_GET_NEXT, _DYN_GET_PLUGIN, _DYN_INITIALIZE, _DYN_IS_INITIALIZED, _DYN_LENGTH, _DYN_PATHNAME, _DYN_PUSH, _DYN_SET_NEXT_PLUGIN, _DYN_SET_TRACE_FLAGS, _DYN_SPAN_ID, _DYN_TEARDOWN, _DYN_TRACE_FLAGS, _DYN_TRACE_ID, _DYN_TRACE_STATE, _DYN__DO_TEARDOWN } from "../__DynamicConstants";
import { STR_CORE, STR_EMPTY, STR_PRIORITY, STR_PROCESS_TELEMETRY, UNDEFINED_VALUE } from "../constants/InternalConstants";
import { createOTelSpanContext } from "../otel/api/trace/spanContext";
import { isOTelTraceState } from "../otel/api/trace/traceState";
import { createW3cTraceState } from "../telemetry/W3cTraceState";
import { generateW3CId } from "../utils/CoreUtils";
import { createElmNodeData } from "../utils/DataCacheHelper";
import { getLocation } from "../utils/EnvUtils";
import { setProtoTypeName } from "../utils/HelperFuncs";
import { isValidSpanId, isValidTraceId } from "../utils/TraceParent";
var pluginStateData = createElmNodeData("plugin");
export function _getPluginState(plugin) {
    return pluginStateData.get(plugin, "state", {}, true);
}
/**
 * Initialize the queue of plugins
 * @param plugins - The array of plugins to initialize and setting of the next plugin
 * @param config - The current config for the instance
 * @param core - THe current core instance
 * @param extensions - The extensions
 */
export function initializePlugins(processContext, extensions) {
    // Set the next plugin and identified the uninitialized plugins
    var initPlugins = [];
    var lastPlugin = null;
    var proxy = processContext[_DYN_GET_NEXT /* @min:%2egetNext */]();
    var pluginState;
    while (proxy) {
        var thePlugin = proxy[_DYN_GET_PLUGIN /* @min:%2egetPlugin */]();
        if (thePlugin) {
            if (lastPlugin && lastPlugin[_DYN_SET_NEXT_PLUGIN /* @min:%2esetNextPlugin */] && thePlugin[STR_PROCESS_TELEMETRY /* @min:%2eprocessTelemetry */]) {
                // Set this plugin as the next for the previous one
                lastPlugin[_DYN_SET_NEXT_PLUGIN /* @min:%2esetNextPlugin */](thePlugin);
            }
            pluginState = _getPluginState(thePlugin);
            var isInitialized = !!pluginState[_DYN_IS_INITIALIZED /* @min:%2eisInitialized */];
            if (thePlugin[_DYN_IS_INITIALIZED /* @min:%2eisInitialized */]) {
                isInitialized = thePlugin[_DYN_IS_INITIALIZED /* @min:%2eisInitialized */]();
            }
            if (!isInitialized) {
                initPlugins[_DYN_PUSH /* @min:%2epush */](thePlugin);
            }
            lastPlugin = thePlugin;
            proxy = proxy[_DYN_GET_NEXT /* @min:%2egetNext */]();
        }
    }
    // Now initialize the plugins
    arrForEach(initPlugins, function (thePlugin) {
        var core = processContext[STR_CORE /* @min:%2ecore */]();
        thePlugin[_DYN_INITIALIZE /* @min:%2einitialize */](processContext.getCfg(), core, extensions, processContext[_DYN_GET_NEXT /* @min:%2egetNext */]());
        pluginState = _getPluginState(thePlugin);
        // Only add the core to the state if the plugin didn't set it (doesn't extend from BaseTelemetryPlugin)
        if (!thePlugin[STR_CORE] && !pluginState[STR_CORE]) {
            pluginState[STR_CORE] = core;
        }
        pluginState[_DYN_IS_INITIALIZED /* @min:%2eisInitialized */] = true;
        delete pluginState[_DYN_TEARDOWN /* @min:%2eteardown */];
    });
}
export function sortPlugins(plugins) {
    // Sort by priority
    return plugins.sort(function (extA, extB) {
        var result = 0;
        if (extB) {
            var bHasProcess = extB[STR_PROCESS_TELEMETRY];
            if (extA[STR_PROCESS_TELEMETRY]) {
                result = bHasProcess ? extA[STR_PRIORITY] - extB[STR_PRIORITY] : 1;
            }
            else if (bHasProcess) {
                result = -1;
            }
        }
        else {
            result = extA ? 1 : -1;
        }
        return result;
    });
    // sort complete
}
/**
 * Teardown / Unload helper to perform teardown/unloading operations for the provided components synchronously or asynchronously, this will call any
 * _doTeardown() or _doUnload() functions on the provided components to allow them to finish removal.
 * @param components - The components you want to unload
 * @param unloadCtx - This is the context that should be used during unloading.
 * @param unloadState - The details / state of the unload process, it holds details like whether it should be unloaded synchronously or asynchronously and the reason for the unload.
 * @param asyncCallback - An optional callback that the plugin must call if it returns true to inform the caller that it has completed any async unload/teardown operations.
 * @returns boolean - true if the plugin has or will call asyncCallback, this allows the plugin to perform any asynchronous operations.
 */
export function unloadComponents(components, unloadCtx, unloadState, asyncCallback) {
    var idx = 0;
    function _doUnload() {
        while (idx < components[_DYN_LENGTH /* @min:%2elength */]) {
            var component = components[idx++];
            if (component) {
                var func = component._doUnload || component[_DYN__DO_TEARDOWN /* @min:%2e_doTeardown */];
                if (isFunction(func)) {
                    if (func[_DYN_CALL /* @min:%2ecall */](component, unloadCtx, unloadState, _doUnload) === true) {
                        return true;
                    }
                }
            }
        }
    }
    return _doUnload();
}
/*#__NO_SIDE_EFFECTS__*/
export function isDistributedTraceContext(obj) {
    return obj &&
        isFunction(obj.getName) &&
        isFunction(obj.getTraceId) &&
        isFunction(obj.getSpanId) &&
        isFunction(obj.getTraceFlags) &&
        isFunction(obj.setName) &&
        isFunction(obj.setTraceId) &&
        isFunction(obj.setSpanId) &&
        isFunction(obj[_DYN_SET_TRACE_FLAGS /* @min:%2esetTraceFlags */]);
}
/**
 * Creates an IDistributedTraceContext instance that ensures a valid traceId is always available.
 * The traceId will be inherited from the parent context if valid, otherwise a new random W3C trace ID is generated.
 *
 * @param parent - An optional parent {@link IDistributedTraceContext} or {@link IOTelSpanContext} to inherit
 * trace context values from. If provided, the traceId and spanId will be copied from the parent if they are valid.
 * When the parent is an {@link IDistributedTraceContext}, it will be set as the parentCtx property to maintain
 * hierarchical relationships and enable parent context updates.
 * When the parent is an {@link IOTelSpanContext}, the parentCtx will be null because OpenTelemetry span contexts
 * are read-only data sources that don't support the same hierarchical management methods as IDistributedTraceContext.
 * The core instance will create a wrapped IDistributedTraceContext instance from the IOTelSpanContext data
 * to enable Application Insights distributed tracing functionality while maintaining OpenTelemetry compatibility.
 *
 * @returns A new IDistributedTraceContext instance with the following behavior:
 * - **traceId**: Always present - either inherited from parent (if valid) or newly generated W3C trace ID
 * - **spanId**: Inherited from parent if valid, otherwise empty string
 * - **traceFlags**: Inherited from parent if available, otherwise undefined
 * - **pageName**: Inherited from parent context or derived from current location, defaults to "_unknown_"
 * - **traceState**: Lazily created W3C trace state, inheriting from parent if available
 *
 * @remarks
 * This function ensures consistent distributed tracing by guaranteeing that every context has a valid traceId,
 * which is essential for the refactored W3C trace state implementation. The spanId may be empty until a
 * specific span is created, which is normal behavior for trace contexts.
 *
 * The distinction between IDistributedTraceContext and IOTelSpanContext parents is important:
 * - IDistributedTraceContext parents enable bidirectional updates and hierarchical management
 * - IOTelSpanContext parents are used only for initial data extraction and OpenTelemetry compatibility
 */
/*#__NO_SIDE_EFFECTS__*/
export function createDistributedTraceContext(parent) {
    var _a;
    var parentCtx = null;
    var initCtx = null;
    var traceId = (parent && isValidTraceId(parent[_DYN_TRACE_ID /* @min:%2etraceId */])) ? parent[_DYN_TRACE_ID /* @min:%2etraceId */] : generateW3CId();
    var spanId = (parent && isValidSpanId(parent[_DYN_SPAN_ID /* @min:%2espanId */])) ? parent[_DYN_SPAN_ID /* @min:%2espanId */] : STR_EMPTY;
    var traceFlags = parent ? parent[_DYN_TRACE_FLAGS /* @min:%2etraceFlags */] : UNDEFINED_VALUE;
    var isRemote = parent ? parent.isRemote : false;
    var pageName = STR_EMPTY;
    var traceState = null;
    if (parent) {
        if (isDistributedTraceContext(parent)) {
            parentCtx = parent;
            pageName = parentCtx.getName();
        }
        else {
            initCtx = parent;
        }
    }
    if (!pageName) {
        pageName = "_unknown_";
        // If we have a location, use that as the page name
        var location_1 = getLocation();
        if (location_1 && location_1[_DYN_PATHNAME /* @min:%2epathname */]) {
            pageName = location_1[_DYN_PATHNAME /* @min:%2epathname */] + (location_1.hash || "");
        }
    }
    function _getName() {
        return pageName;
    }
    function _setPageNameFn(updateParent) {
        return function (newValue) {
            if (updateParent) {
                parentCtx && parentCtx.setName(newValue);
            }
            pageName = newValue;
        };
    }
    function _getTraceId() {
        return traceId;
    }
    function _setTraceIdFn(updateParent) {
        return function (newValue) {
            if (updateParent) {
                parentCtx && parentCtx.setTraceId(newValue);
            }
            if (isValidTraceId(newValue)) {
                traceId = newValue;
            }
        };
    }
    function _getSpanId() {
        return spanId;
    }
    function _setSpanIdFn(updateParent) {
        return function (newValue) {
            if (updateParent) {
                parentCtx && parentCtx.setSpanId(newValue);
            }
            if (isValidSpanId(newValue)) {
                spanId = newValue;
            }
        };
    }
    function _getTraceFlags() {
        return traceFlags;
    }
    function _setTraceFlagsFn(updateParent) {
        return function (newTraceFlags) {
            if (updateParent) {
                parentCtx && parentCtx[_DYN_SET_TRACE_FLAGS /* @min:%2esetTraceFlags */](newTraceFlags);
            }
            traceFlags = newTraceFlags;
        };
    }
    function _getTraceState() {
        if (!traceState) {
            if (!parentCtx) {
                // The passed in parent was not an IDistributedTraceContext
                if (initCtx) {
                    if (isOTelTraceState(initCtx[_DYN_TRACE_STATE /* @min:%2etraceState */])) {
                        // This looks like an IOTelSpanContext, so we have to just use the passed traceState as-is as it doesn't support
                        // the W3cTraceState heirarchy methods
                        traceState = createW3cTraceState(initCtx[_DYN_TRACE_STATE /* @min:%2etraceState */].serialize() || STR_EMPTY, parentCtx ? parentCtx[_DYN_TRACE_STATE /* @min:%2etraceState */] : undefined);
                    }
                    else {
                        // This looks like an IDistributedTraceInit, so we can create a new W3cTraceState
                        traceState = createW3cTraceState(STR_EMPTY, initCtx[_DYN_TRACE_STATE /* @min:%2etraceState */] || (parentCtx ? parentCtx[_DYN_TRACE_STATE /* @min:%2etraceState */] : undefined));
                    }
                }
            }
            if (!traceState) {
                traceState = createW3cTraceState(STR_EMPTY, parentCtx ? parentCtx[_DYN_TRACE_STATE /* @min:%2etraceState */] : undefined);
            }
        }
        return traceState;
    }
    var otelSpanCtx = null;
    var traceCtx = setProtoTypeName((_a = {
            getName: _getName,
            setName: _setPageNameFn(true),
            getTraceId: _getTraceId,
            setTraceId: _setTraceIdFn(true),
            getSpanId: _getSpanId,
            setSpanId: _setSpanIdFn(true),
            getTraceFlags: _getTraceFlags
        },
        _a[_DYN_SET_TRACE_FLAGS /* @min:setTraceFlags */] = _setTraceFlagsFn(true),
        _a.traceId = traceId,
        _a.spanId = spanId,
        _a.traceFlags = traceFlags,
        _a.traceState = traceState,
        _a.isRemote = isRemote,
        _a.pageName = pageName,
        _a.getOTelSpanContext = function () {
            if (!otelSpanCtx) {
                // Lazily create the OTel Span Context
                otelSpanCtx = createOTelSpanContext(traceCtx);
            }
            return otelSpanCtx;
        },
        _a), "DistributedTraceContext");
    return objDefineProps(traceCtx, {
        pageName: {
            g: _getName,
            s: _setPageNameFn(false)
        },
        traceId: {
            g: _getTraceId,
            s: _setTraceIdFn(false)
        },
        spanId: {
            g: _getSpanId,
            s: _setSpanIdFn(false)
        },
        traceFlags: {
            g: _getTraceFlags,
            s: _setTraceFlagsFn(false)
        },
        isRemote: {
            v: isRemote,
            w: false
        },
        traceState: {
            g: _getTraceState
        },
        parentCtx: {
            g: function () { return parentCtx; }
        },
        _parent: {
            g: function () {
                var result;
                if (parentCtx) {
                    result = {
                        t: "traceCtx",
                        v: parentCtx
                    };
                }
                else if (initCtx) {
                    result = {
                        t: "initCtx",
                        v: initCtx
                    };
                }
                return result;
            }
        }
    });
}
//# sourceMappingURL=TelemetryHelpers.js.map